import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScanFace, Lock, LogOut, Loader2, CheckCircle2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authAxios = axios.create({ baseURL: API, withCredentials: true });

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [acting, setActing] = useState("");

  const load = async () => {
    try {
      const [o, s] = await Promise.all([authAxios.get("/admin/orders"), authAxios.get("/admin/stats")]);
      setOrders(o.data);
      setStats(s.data);
      setAuthed(true);
    } catch (err) {
      console.error("Erro ao carregar dados do admin:", err);
      setAuthed(false);
    }
  };

  useEffect(() => {
    authAxios.get("/admin/me").then(load).catch(() => setAuthed(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async () => {
    setLoading(true);
    try {
      await authAxios.post("/admin/login", { email, password });
      await load();
    } catch (err) {
      console.error("Falha no login:", err);
      toast.error("Credenciais inválidas");
    }
    setLoading(false);
  };

  const logout = async () => {
    try { await authAxios.post("/admin/logout"); } catch (err) { console.error(err); }
    setAuthed(false);
    setOrders([]);
    setStats(null);
  };

  const markPaid = async (orderId) => {
    setActing(orderId + ":paid");
    try {
      await authAxios.post(`/admin/orders/${orderId}/mark-paid`);
      toast.success("Pedido liberado! O estudo está sendo gerado e o e-mail será enviado ao concluir.");
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Falha ao liberar o pedido");
    }
    setActing("");
  };

  const resendEmail = async (orderId) => {
    setActing(orderId + ":mail");
    try {
      await authAxios.post(`/admin/orders/${orderId}/resend-email`);
      toast.success("E-mail reenviado ao cliente.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Falha ao reenviar o e-mail");
    }
    setActing("");
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (authed !== true) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <Card className="w-full max-w-sm p-8 rounded-2xl border-line bg-white">
          <div className="flex items-center gap-2 font-serif text-xl mb-1"><ScanFace className="w-5 h-5 text-accent" /> VisagismoIA</div>
          <p className="text-charcoal-500 text-sm mb-6">Painel administrativo</p>
          <div className="space-y-3">
            <Input data-testid="admin-email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input data-testid="admin-password" type="password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} />
            <Button onClick={login} disabled={loading} data-testid="admin-login-btn" className="w-full rounded-full bg-charcoal-900 text-cream hover:bg-charcoal-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Entrar</>}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-line bg-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-serif text-lg"><ScanFace className="w-4 h-4 text-accent" /> VisagismoIA · Admin</div>
          <div className="flex items-center gap-3">
            {stats && <Badge variant="outline" className="border-accent text-accent">Modo: {stats.mode}</Badge>}
            <Button onClick={logout} variant="outline" size="sm" data-testid="admin-logout" className="rounded-full"><LogOut className="w-4 h-4 mr-1" /> Sair</Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { l: "Pedidos", v: stats.total_orders },
              { l: "Pagos", v: stats.paid_orders },
              { l: "Entregues", v: stats.delivered },
              { l: "Receita", v: `R$${Number(stats.revenue_brl).toFixed(0)}` },
            ].map((c) => (
              <Card key={c.l} className="p-6 rounded-2xl border-line bg-white">
                <p className="text-xs tracking-widest uppercase text-charcoal-500">{c.l}</p>
                <p className="font-serif text-3xl mt-1">{c.v}</p>
              </Card>
            ))}
          </div>
        )}

        <Card className="rounded-2xl border-line bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pedido</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Gênero</TableHead>
                <TableHead>Pagamento</TableHead>
                <TableHead>Geração</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-charcoal-500 py-10">Nenhum pedido ainda.</TableCell></TableRow>}
              {orders.map((o) => (
                <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                  <TableCell className="font-mono text-xs">{o.order_id.slice(0, 8)}</TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{o.name || "—"}</div>
                    <div className="text-charcoal-500 text-xs">{o.email || "—"}</div>
                  </TableCell>
                  <TableCell className="capitalize">{o.gender}</TableCell>
                  <TableCell>
                    <Badge className={o.payment_status === "paid" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
                      {o.payment_status === "paid" ? "Pago" : "Pendente"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{o.generation_status === "done" ? "Entregue" : o.generation_status === "processing" ? "Processando" : o.generation_status === "error" ? "Erro" : "—"}</Badge>
                  </TableCell>
                  <TableCell className="text-charcoal-500 text-sm">{o.created_at ? new Date(o.created_at).toLocaleString("pt-BR") : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {o.payment_status !== "paid" && (
                        <Button
                          size="sm"
                          data-testid={`mark-paid-${o.order_id}`}
                          onClick={() => markPaid(o.order_id)}
                          disabled={acting === o.order_id + ":paid"}
                          className="rounded-full bg-green-600 hover:bg-green-700 text-white h-8"
                        >
                          {acting === o.order_id + ":paid" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Liberar PDF</>}
                        </Button>
                      )}
                      {o.payment_status === "paid" && o.generation_status === "done" && o.email && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`resend-email-${o.order_id}`}
                          onClick={() => resendEmail(o.order_id)}
                          disabled={acting === o.order_id + ":mail"}
                          className="rounded-full h-8"
                        >
                          {acting === o.order_id + ":mail" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Reenviar e-mail</>}
                        </Button>
                      )}
                      {o.payment_status === "paid" && o.generation_status !== "done" && (
                        <span className="text-charcoal-500 text-xs">Gerando…</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
